import React from "react";
import { useArtifactStore } from "@/stores/artifact-store";
import { ExternalLink } from "lucide-react";

// Helper functions
const isImageUrl = (url: string): boolean => /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);

const isVideoUrl = (url: string): boolean => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

const isEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Enhanced link renderer
export const LinkWithFavicon = ({
    href,
    children,
    threadId,
    ...props
}: React.ComponentPropsWithoutRef<"a"> & { href?: string; threadId?: string }) => {
    const { addArtifact } = useArtifactStore();

    if (!href) return <a {...props}>{children}</a>;

    if (isImageUrl(href)) {
        return (
            <div className="my-4">
                <img
                    src={href}
                    alt={typeof children === "string" ? children : "External image"}
                    className="rounded-md shadow-sm"
                    loading="lazy"
                    onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const parent = target.parentElement;
                        if (parent) {
                            parent.innerHTML = `<a href="${href}" target="_blank" rel="noreferrer" class="text-primary underline">🖼️ ${children}</a>`;
                        }
                    }}
                />
            </div>
        );
    }

    if (isVideoUrl(href)) {
        return (
            <div className="my-4">
                <video
                    controls
                    className="rounded-md shadow-sm"
                    onError={(e) => {
                        const target = e.target as HTMLVideoElement;
                        const parent = target.parentElement;
                        if (parent) {
                            parent.innerHTML = `<a href="${href}" target="_blank" rel="noreferrer" class="text-primary underline">🎥 ${children}</a>`;
                        }
                    }}
                >
                    <source src={href} />
                    <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">
                        🎥 {children}
                    </a>
                </video>
            </div>
        );
    }

    if (isEmail(href)) {
        return (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-primary underline transition-colors hover:text-primary/80"
                {...props}
            >
                {children}
            </a>
        );
    }

    // Regular link with favicon and artifact button
    let faviconUrl = "";
    let domain = "";
    try {
        const { hostname } = new URL(href);
        domain = hostname.replace(/^www\./, "");
        faviconUrl = `https://www.google.com/s2/favicons?domain=${hostname}&sz=16`;
    } catch {
        // Invalid URL, just show as text
    }

    const handleOpenInArtifact = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!threadId) {
            console.warn("Cannot open link in artifact: threadId is missing");
            return;
        }

        const linkTitle = typeof children === "string" ? children : domain || href;
        
        addArtifact({
            type: "link",
            title: linkTitle,
            content: { 
                url: href,
                favicon: faviconUrl
            },
            threadId,
        });
    };

    return (
        <span className="inline-flex items-center gap-1.5">
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-primary underline transition-colors hover:text-primary/80"
                {...props}
            >
                {faviconUrl && (
                    <img src={faviconUrl} alt="" width={16} height={16} className="my-0 mr-2 rounded-none shadow-none" />
                )}
                {children}
            </a>
            {threadId && (
                <button
                    onClick={handleOpenInArtifact}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                    title="Open in Slate"
                    aria-label="Open in Slate"
                >
                    <ExternalLink className="h-3 w-3" />
                    Open in Slate
                </button>
            )}
        </span>
    );
};